/**
 * Modelo de tabela compartilhado entre DataCuidados, Nuvem de Perguntas e DataStories.
 *
 * As três telas recebem os dados em formatos diferentes (colunas do servidor, pares
 * `headers`/`rows`, ou as próprias options do ECharts). Todas convertem para este modelo
 * antes de renderizar ou exportar, para que transposição, seleção de colunas, CSV e XLSX
 * tenham um único caminho de código.
 *
 * As linhas guardam o valor **cru** — `format` é só para exibição. Exports levam o valor
 * cru para não perder precisão nem embutir separador de milhar.
 */
export interface TableColumn {
  key: string;
  label: string;
  /** Alinha à direita e usa fonte tabular na renderização. */
  numeric?: boolean;
  /** Formatação de exibição; o export continua usando o valor cru. */
  format?: (value: unknown, row: TableRow) => string;
}

export type TableRow = Record<string, unknown>;

export interface TableModel {
  columns: TableColumn[];
  rows: TableRow[];
}

/** Chave da coluna que guarda os rótulos originais depois de transpor. */
export const TRANSPOSED_LABEL_KEY = '__label';

export function isEmptyTable(model: TableModel | null | undefined): boolean {
  return !model || model.columns.length === 0 || model.rows.length === 0;
}

/** Texto de exibição de uma célula, aplicando `format` quando a coluna define um. */
export function formatTableCell(column: TableColumn, row: TableRow): string {
  const value = row[column.key];
  if (column.format) return column.format(value, row);
  return value == null ? '' : String(value);
}

/**
 * Constrói o modelo a partir do par `headers`/`rows` usado pela Nuvem de Perguntas.
 * A primeira coluna costuma ser o rótulo do eixo X e vem sem cabeçalho.
 */
export function tableFromHeadersRows(
  headers: readonly string[],
  rows: readonly (readonly unknown[])[],
  options: { firstColumnLabel?: string } = {},
): TableModel {
  const columns: TableColumn[] = headers.map((header, index) => ({
    key: `c${index}`,
    label: index === 0 ? (header || options.firstColumnLabel || '') : header,
    numeric: index > 0,
  }));

  return {
    columns,
    rows: rows.map((cells) => {
      const row: TableRow = {};
      columns.forEach((column, index) => {
        row[column.key] = cells[index] ?? null;
      });
      return row;
    }),
  };
}
